import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { showToast } from '../components/Toast';
import MembershipPanel from '../components/membership/MembershipPanel';
import RecordCashSheet from '../components/membership/RecordCashSheet';
import apiService from '../services/apiService';
import UserAvatar from '../components/UserAvatar';
import ProfilePhotoControl from '../components/ProfilePhotoControl';
import { DSCard, DSCardFooterButton, DSIcon, DSInput, DSSelect, DSTextarea, DSSheet } from '../design-system/primitives';
import { useManageUser, TOURNAMENT_FEE } from '../hooks/useManageUser';
import { formatHandicap } from '../utils/pingolfScoring';
import './ManageUser.css';
import { matchplayProfileUrl, ifpaProfileUrl } from '../utils/profileUrls';
import BadgeIcon from '../components/BadgeIcon';

// The admin single-user screen, organised by task rather than by data model: the job
// done most often (a charge at the machine) is the first thing on screen, and the rest
// sits behind a tab strip. All data and mutations still come from useManageUser; this
// file is presentation only.
//
// Every edit, charge and confirmation is a bottom sheet. There is deliberately no
// whole-page edit form: editing happens where the field lives, so the page never
// reflows under the admin mid-task.

const MINUS = '−'; // U+2212, the real minus sign, for money displays
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SLOTS = ['AM', 'PM', 'Eve'];

const TECH_TIERS = [
  { value: 0, label: '0: No machine access' },
  { value: 1, label: '1: Glass access' },
  { value: 2, label: '2: Routine maintenance' },
  { value: 3, label: '3: Repair tech' },
  { value: 4, label: '4: Senior tech' }
];

// Administrator is the only organisation-wide role. The old four-option
// selector offered 'member' and 'guest', which were never checked anywhere in
// the codebase and granted nothing, while real membership lives in the
// membership status. 'host' moved to a feature-role grant alongside Task
// Delegate and Maintenance Organizer. See requirements section 14.
const ADMIN_ROLE = 'admin';
const NON_ADMIN_ROLE = 'member';

const GUEST_PASS_PRICE = 15;

function money(value) {
  const n = Number(value) || 0;
  return `${n < 0 ? MINUS : ''}$${Math.abs(n).toFixed(2)}`;
}

function signedMoney(value) {
  const n = Number(value) || 0;
  return `${n > 0 ? '+' : ''}${money(n)}`;
}

function titleCase(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// The stored shape is either an array of "Tuesday-AM" strings or an object keyed by
// day. Both have to land in the same weekday grid, so they are normalised here.
function normalizeAvailability(raw) {
  if (!raw) return {};
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (err) {
      return {};
    }
  }
  const byDay = {};
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      const [day, slot] = String(entry).split('-');
      if (!day || !slot) return;
      byDay[day.slice(0, 3)] = byDay[day.slice(0, 3)] || [];
      byDay[day.slice(0, 3)].push(slot);
    });
    return byDay;
  }
  Object.entries(value).forEach(([day, slots]) => {
    byDay[day.slice(0, 3)] = Array.isArray(slots) ? slots : [];
  });
  return byDay;
}

function parseContributionAreas(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

/* ---------------- small presentational pieces ---------------- */

function Sheet({
  title,
  body,
  blocked,
  record,
  secret,
  secretNote,
  onCopySecret,
  children,
  confirmLabel,
  confirmDisabled,
  danger,
  onConfirm,
  cancelLabel = 'Cancel',
  onClose
}) {
  const footer = (
    <div className="mu-sheet-footer">
      {confirmLabel && (
        <button
          type="button"
          className={`mu-sheet-confirm${danger ? ' mu-sheet-confirm--danger' : ''}`}
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      )}
      <button type="button" className="mu-sheet-cancel" onClick={onClose}>
        {cancelLabel}
      </button>
    </div>
  );

  return (
    <DSSheet title={title} onClose={onClose} footer={footer}>
      {body && <p className="mu-sheet-body">{body}</p>}
      {blocked && (
        <div className="mu-sheet-blocked">
          <p className="mu-sheet-blocked-title">{blocked.title}</p>
          <p className="mu-sheet-blocked-body">{blocked.body}</p>
        </div>
      )}
      {record && record.length > 0 && (
        <div className="mu-record">
          {record.map((row) => (
            <div className="mu-record-row" key={row.label}>
              <span className="mu-record-label">{row.label}</span>
              <span className="mu-record-value">{row.value}</span>
            </div>
          ))}
        </div>
      )}
      {children && <div className="mu-sheet-fields">{children}</div>}
      {secret && (
        <div className="mu-secret">
          <p className="mu-secret-label">Temporary password</p>
          <div className="mu-secret-row">
            <code className="mu-secret-value">{secret}</code>
            <button type="button" className="mu-mini-button" onClick={onCopySecret}>
              Copy
            </button>
          </div>
          <p className="mu-secret-note">{secretNote}</p>
        </div>
      )}
    </DSSheet>
  );
}

function SheetCheck({ label, checked, onChange }) {
  return (
    <button
      type="button"
      className={`mu-check${checked ? ' is-on' : ''}`}
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
    >
      <span className="mu-check-box">{checked ? <DSIcon name="check" size="sm" /> : null}</span>
      <span className="mu-check-label">{label}</span>
    </button>
  );
}

function StatTile({ label, value, tone }) {
  return (
    <div className={`mu-stat${tone === 'warning' ? ' mu-stat--warning' : ''}`}>
      <span className="mu-stat-label">{label}</span>
      <span className={`mu-stat-value${tone ? ` is-${tone}` : ''}`}>{value}</span>
    </div>
  );
}

function SettingRow({ label, help, value, tone = 'default', locked, onAct }) {
  return (
    <div className="mu-setting-row">
      <span className="mu-row-main">
        <span className="mu-setting-label-row">
          <span className="mu-setting-label">{label}</span>
          {locked && <span className="mu-lock-pill">Admin</span>}
        </span>
        <span className="mu-setting-help">{help}</span>
      </span>
      <button type="button" className={`mu-row-button mu-row-button--${tone}`} onClick={onAct}>
        {value}
      </button>
    </div>
  );
}

/* ---------------- page ---------------- */

function ManageUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    user, membershipSettings, application, applicationHistory, selectedApplicationId,
    signedDocuments,
    memberEmails,
    transactions, loading, error,
    isTaskDelegate, isMaintenanceOrganizer,
    userBadges, awardableBadges,
    showAwardBadgeModal, setShowAwardBadgeModal,
    badgeToAwardId, setBadgeToAwardId,
    badgeSearchTerm, setBadgeSearchTerm,
    awardingBadge,
    showAllTransactions, setShowAllTransactions,
    showCreditsModal, setShowCreditsModal,
    showPaymentModal, setShowPaymentModal,
    showPasswordResetModal, setShowPasswordResetModal,
    showTabSettingsModal, setShowTabSettingsModal,
    showHandicapSeedModal, setShowHandicapSeedModal,
    showPartOrderModal, setShowPartOrderModal,
    showGuestPassModal, setShowGuestPassModal,
    showEditTransactionModal, setShowEditTransactionModal,
    showEditPaymentModal, setShowEditPaymentModal,
    showDeletePaymentModal, setShowDeletePaymentModal,
    selectedPayment, setSelectedPayment,
    confirmState, setConfirmState,
    confirmSubmitting,
    isEditingProfile,
    profileForm, setProfileForm,
    passwordResetResult, setPasswordResetResult,
    partOrderForm, setPartOrderForm,
    guestPassForm, setGuestPassForm,
    chargeForm, setChargeForm,
    matchplayProfile,
    creditsForm, setCreditsForm,
    editTransactionForm, setEditTransactionForm,
    paymentForm, setPaymentForm,
    editPaymentForm, setEditPaymentForm,
    tabSettingsForm, setTabSettingsForm,
    handicapSeedForm, setHandicapSeedForm,
    handleAddCredits,
    handlePartOrderCharge,
    handleGuestPassPurchase,
    handleDeskCharge,
    loadUserData,
    handleOpenEditTransaction,
    handleEditTransaction,
    handleDeleteTransaction,
    handleRecordPayment,

    handleEditPayment,

    handleDeletePayment,

    showMemberSinceSheet, setShowMemberSinceSheet,
    memberSinceDraft, setMemberSinceDraft,
    handleOpenMemberSince,
    handleUpdateMemberSince,
    handleOpenTabSettingsModal,
    handleUpdateTabSettings,
    handleOpenHandicapSeedModal,
    handleUpdateHandicapSeed,
    handleToggleStatus,
    handleTechTierChange,
    handleToggleTaskDelegate,
    handleToggleMaintenanceOrganizer,
    handleOpenAwardBadgeModal,
    handleAwardBadge,
    handleRevokeBadge,
    handleRoleChange,
    handleRegenerateQRCode,
    handleEditProfile,
    handleCancelEditProfile,
    handleSaveProfile,
    handleMarkOrientationScheduled,
    handleMarkOrientationComplete,
    handleMarkPaymentSetupComplete,
    handleMarkMasterLockComplete,
    handleUndoOrientationScheduled,
    handleUndoOrientationComplete,
    handleUndoPaymentSetup,
    handleUndoMasterLockSetup,
    handleCloseApplication,
    handleSwitchApplication,
    handleDeleteUser,
    handleSyncMatchplayProfile,
    handleResetPassword,
    formatDate,
    formatMonth,
    formatDateTime
  } = useManageUser(userId, navigate);

  // Which field group the profile sheet is editing. The hook still owns the form and
  // the save, so a per-field sheet is just a narrower view of the same profileForm.
  const [profileField, setProfileField] = useState(null);
  const [picker, setPicker] = useState(null);
  const [openTx, setOpenTx] = useState(null);
  // The member as RecordCashSheet wants it, once their membership status has
  // loaded. Null while closed.
  const [duesFor, setDuesFor] = useState(null);

  const requestedTab = searchParams.get('tab');

  const creditBalance = useMemo(
    () => transactions.reduce((sum, txn) => sum + parseFloat(txn.amount), 0),
    [transactions]
  );

  if (loading) {
    return (
      <div className="mu-page">
        <div className="mu-shell">
          <p className="mu-loading">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="mu-page">
        <div className="mu-shell">
          <p className="mu-error">{error || 'User not found'}</p>
          <div className="mu-panel">
            <Link to="/users" className="mu-outline-button" style={{ textAlign: 'center', lineHeight: '46px' }}>
              Back to Users
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const rawProfile = matchplayProfile?.rawProfile;
  const rating = rawProfile?.rating;
  const matchplaySynced = Boolean(user.matchplay_user_id && rating);
  const ifpaId = user.ifpa_player_id || rawProfile?.user?.ifpaId || null;
  const tabEnabled = Boolean(user.tab_enabled);
  const negative = creditBalance < 0;
  const hasStern = Boolean(user.stern_username && String(user.stern_username).trim());
  const isAdmin = user.role === 'admin';

  const tabLimitLabel = user.tab_limit === null || user.tab_limit === undefined
    ? 'no limit'
    : `$${parseFloat(user.tab_limit).toFixed(2)} limit`;

  const stepsDone = application
    ? [
      Boolean(application.orientation_scheduled_date),
      Boolean(application.orientation_completed),
      Boolean(application.payment_setup_complete),
      Boolean(application.master_lock_setup)
    ]
    : [];
  const doneCount = stepsDone.filter(Boolean).length;

  const tabs = [
    { id: 'desk', label: 'Desk' },
    { id: 'overview', label: 'Overview' },
    { id: 'money', label: 'Money' },
    { id: 'access', label: 'Access' },
    ...(application ? [{ id: 'onboarding', label: 'Onboarding', count: `${doneCount}/4` }] : []),
    { id: 'support', label: 'Support' }
  ];

  const tab = tabs.some((t) => t.id === requestedTab) ? requestedTab : 'desk';
  const goTab = (id) => setSearchParams(id === 'desk' ? {} : { tab: id }, { replace: true });

  /* ---------- sheet openers ---------- */

  const openProfileSheet = (field) => () => {
    handleEditProfile();
    setProfileField(field);
  };

  const closeProfileSheet = () => {
    handleCancelEditProfile();
    setProfileField(null);
  };

  const confirmThen = (config) => {
    setConfirmState({
      title: config.title,
      message: config.message,
      confirmLabel: config.confirmLabel,
      danger: config.danger,
      onConfirm: async () => {
        await config.run();
        setConfirmState(null);
      }
    });
  };

  // Dues from the Desk default to paying from this member's own credits: that
  // is the desk task. Status is fetched first so months already paid are
  // greyed out and a Square payer gets warned before being charged twice.
  const openDuesSheet = async () => {
    try {
      const { data } = await apiService.getMemberStatus(user.id);
      const evidence = data.evidence || {};
      setDuesFor({
        userId: user.id,
        name: `${user.preferred_name || user.first_name} ${user.last_name}`,
        photo_url: user.photo_url,
        detail: evidence.cashPaidThrough
          ? `Paid through ${formatMonth(evidence.cashPaidThrough)}.`
          : 'No cash payment recorded yet.',
        method: data.paymentMethod,
        paidMonths: (evidence.recentPayments || [])
          .filter((payment) => !payment.coversUserId)
          .map((payment) => String(payment.month).slice(0, 7))
      });
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not load membership status', 'error');
    }
  };

  const openPartOrderSheet = () => {
    setPartOrderForm({ amount: '', description: 'Part order' });
    setShowPartOrderModal(true);
  };

  /* ---------- panels ---------- */

  const deskActions = [
    { key: 'guest', icon: 'ticket', label: 'Guest day pass', hint: '$15.00 fixed', onClick: () => setShowGuestPassModal(true) },
    { key: 'part', icon: 'wrench', label: 'Part order', hint: 'Any amount', onClick: openPartOrderSheet },
    { key: 'dues', icon: 'card', label: 'Membership dues', hint: 'Pay from credits', onClick: openDuesSheet },
    {
      key: 'tournament',
      icon: 'trophy',
      label: 'Tournament fee',
      hint: `$${TOURNAMENT_FEE.toFixed(2)} fixed`,
      onClick: () => setChargeForm({ kind: 'tournament', amount: '', description: '' })
    },
    {
      key: 'service',
      icon: 'machine',
      label: 'Service',
      hint: 'Work on their machine',
      onClick: () => setChargeForm({ kind: 'service', amount: '', description: '' })
    },
    {
      key: 'other',
      icon: 'dollar',
      label: 'Other',
      hint: 'Say what, any amount',
      onClick: () => setChargeForm({ kind: 'other', amount: '', description: '' })
    }
  ];

  const txTrail = (txn) => {
    const chips = [];
    const grantedBy = txn.granted_by_name || txn.granted_by_username;
    if (grantedBy) chips.push(`Granted by ${grantedBy}`);
    if (txn.device_name) chips.push(`Kiosk: ${txn.device_name}`);
    return chips;
  };

  const renderDesk = () => (
    <>
      {/* The whole card is the Add credits button: it is the most common desk
          task, and the balance is what you look at before doing it. Spans, not
          paragraphs, because a button may only hold inline content. */}
      <button
        type="button"
        className="mu-card mu-balance-card"
        onClick={() => setShowCreditsModal(true)}
      >
        <span className="mu-balance-main">
          <span className="mu-section-label">Credit balance</span>
          <span className="mu-balance-row">
            <span className={`mu-balance${negative ? ' is-negative' : ''}`}>{money(creditBalance)}</span>
            {negative && tabEnabled && (
              <span className="mu-balance-chip mu-balance-chip--warning">Tab active · {tabLimitLabel}</span>
            )}
            {negative && !tabEnabled && (
              <span className="mu-balance-chip mu-balance-chip--error">Purchases blocked</span>
            )}
          </span>
          <span className="mu-balance-note">
            {!negative && 'Available to spend at any machine or kiosk.'}
            {negative && tabEnabled && 'Running on the tab. Charges still go through up to the limit.'}
            {negative && !tabEnabled && 'Negative with no tab. Purchases are blocked until credits are added.'}
          </span>
        </span>
        <span className="mu-balance-add">
          <DSIcon name="plus" label="Add credits" />
        </span>
      </button>

      <section>
        <p className="mu-section-label">Charge to account</p>
        <div className="mu-tile-grid">
          {deskActions.map((action) => (
            <button type="button" key={action.key} className="mu-action-tile" onClick={action.onClick}>
              <span className="mu-action-icon"><DSIcon name={action.icon} /></span>
              <span className="mu-action-text">
                <span className="mu-action-label">{action.label}</span>
                <span className="mu-action-hint">{action.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <DSCard
        frame="bars"
        density="compact"
        flush
        title="Last 3 transactions"
        actions={(
          <button type="button" className="mu-text-action" onClick={() => goTab('money')}>
            All in Money <DSIcon name="arrowRight" size="sm" />
          </button>
        )}
      >
        {transactions.length === 0 && <p className="mu-empty-line">No transactions yet.</p>}
        {transactions.slice(0, 3).map((txn) => {
          const amount = parseFloat(txn.amount);
          const trail = txTrail(txn);
          return (
            <div className="mu-recent-row" key={txn.id}>
              <span className="mu-row-main">
                <span className="mu-row-title">{txn.description || (amount >= 0 ? 'Credit added' : 'Debit')}</span>
                <span className="mu-row-meta">
                  {formatDateTime(txn.created_at)}{trail.length > 0 ? ` · ${trail[0]}` : ''}
                </span>
              </span>
              <span className={`mu-amount${amount > 0 ? ' is-credit' : ''}`}>{signedMoney(amount)}</span>
            </div>
          );
        })}
      </DSCard>
    </>
  );

  // A merged account keeps the other account's address as an alias, and that
  // alias is often the one Square is still billing under - so showing only the
  // primary made a merged member look like the merge had not happened.
  const otherEmails = (memberEmails || [])
    .filter((row) => row.email && row.email.toLowerCase() !== (user.email || '').toLowerCase())
    .map((row) => row.email);

  const contactRows = [
    { label: 'Email', value: user.email, edit: openProfileSheet('email') },
    ...(otherEmails.length > 0
      ? [{
        label: otherEmails.length === 1 ? 'Also known as' : 'Also known as',
        value: otherEmails.join(', '),
        hint: 'Payments made under these addresses count toward this membership.'
      }]
      : []),
    { label: 'Phone', value: user.phone_number || 'Not set', edit: openProfileSheet('phone') },
    {
      label: 'Member since',
      value: membershipSettings?.start_date ? formatDate(membershipSettings.start_date) : 'Not tracked',
      edit: handleOpenMemberSince
    }
  ];

  const compRows = [
    {
      key: 'stern',
      label: 'Stern username',
      value: hasStern ? user.stern_username : 'Not set, blocks competition entry',
      warn: !hasStern,
      action: hasStern ? 'Edit' : 'Add',
      edit: openProfileSheet('stern')
    },
    {
      key: 'matchplay',
      label: 'MatchPlay ID',
      value: user.matchplay_user_id || 'Not set',
      action: 'Edit',
      link: matchplayProfileUrl(user.matchplay_user_id),
      edit: openProfileSheet('matchplay')
    },
    {
      key: 'ifpa',
      label: 'IFPA player ID',
      value: ifpaId || 'Not set',
      action: 'Edit',
      link: ifpaProfileUrl(ifpaId),
      edit: openProfileSheet('ifpa')
    }
  ];

  // Admin sees both spellings on purpose. "Applied" is the number the player reads on their
  // scorecard (strokes added to or taken off the card); "scoring avg" is the raw vs-par mean
  // the model stores. They are negations of each other, so showing only one guarantees a
  // support conversation at cross purposes with whatever the player is looking at. The seed
  // is shown in applied-strokes units, matching the field that sets it.
  const golfStats = [
    { label: 'Applied handicap', value: formatHandicap(user.pingolf_playing_handicap) },
    {
      label: 'Scoring avg (vs par)',
      value: user.pingolf_handicap_index !== null && user.pingolf_handicap_index !== undefined
        ? Number(user.pingolf_handicap_index).toFixed(2)
        : '-'
    },
    { label: 'Cards', value: user.pingolf_handicap_rounds || 0 },
    {
      label: 'Seed',
      value: user.pingolf_handicap_seed !== null && user.pingolf_handicap_seed !== undefined
        ? `${formatHandicap(user.pingolf_handicap_seed, { decimals: 1 })} / ${user.pingolf_handicap_seed_weight} rds`
        : 'None'
    }
  ];

  const renderOverview = () => (
    <>
      {/* Shown with the host bio on the hallway sign on nights this member hosts. */}
      <section className="mu-card mu-card--padded">
        <div className="mu-section-head">
          <p className="mu-section-label">Photo</p>
        </div>
        <ProfilePhotoControl
          person={user}
          onChange={() => loadUserData()}
          hint="Shown with their host bio on the hallway sign on nights they host."
        />
      </section>

      {/* Always shown, so an admin can write a bio for someone who has none yet. */}
      <section className="mu-card mu-bio-card">
        <div className="mu-section-head">
          <p className="mu-section-label">Host bio</p>
          <button type="button" className="mu-text-action" onClick={openProfileSheet('bio')}>
            {user.host_profile ? 'Edit' : 'Add'}
          </button>
        </div>
        {user.host_profile
          ? <p className="mu-bio-body">{user.host_profile}</p>
          : <p className="mu-empty-body">No host bio yet. It shows on their event pages and on the hallway sign on nights they host.</p>}
      </section>

      {/* Which documents this member has signed, and when. The admin
          acknowledgments view returned nothing for everybody for as long as it
          existed, so "did they ever agree to the rules?" had no answer short of
          a database query. */}
      {signedDocuments.length > 0 && (
        <section className="mu-card mu-card--padded">
          <p className="mu-section-label" style={{ marginBottom: '10px' }}>Signed documents</p>
          <div className="mu-kv-list">
            {signedDocuments.map((doc) => (
              <div className="mu-kv-row" key={doc.documentKey}>
                <span className="mu-kv-label">{doc.label}</span>
                <span className="mu-kv-value">
                  {doc.acknowledgedAt
                    ? new Date(doc.acknowledgedAt).toLocaleDateString()
                    : 'Never signed'}
                  {doc.required && ' - being asked again'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mu-card mu-card--padded">
        <p className="mu-section-label" style={{ marginBottom: '10px' }}>Contact</p>
        <div className="mu-kv-list">
          {contactRows.map((row) => (
            <div className="mu-kv-row" key={row.label}>
              <span className="mu-kv-label">{row.label}</span>
              <span className="mu-kv-value">
                {row.value}
                {row.hint && <span className="mu-kv-hint">{row.hint}</span>}
              </span>
              {row.edit && (
                <button type="button" className="mu-mini-button" onClick={row.edit}>Edit</button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="mu-section-label">Competition identity</p>
        <div className="mu-comp-list">
          {compRows.map((row) => (
            <div className={`mu-comp-row${row.warn ? ' is-warning' : ''}`} key={row.key}>
              <span className="mu-row-main">
                <span className="mu-comp-label">{row.label}</span>
                <span className="mu-comp-value">{row.value}</span>
              </span>
              {row.link && (
                <a
                  className="mu-row-button mu-row-button--link"
                  href={row.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${row.label} profile in a new tab`}
                >
                  ↗
                </a>
              )}
              <button type="button" className="mu-row-button" onClick={row.edit}>{row.action}</button>
            </div>
          ))}
        </div>
      </section>

      {matchplaySynced ? (
        <section className="mu-card mu-card--padded">
          <div className="mu-section-head">
            <p className="mu-section-label">MatchPlay</p>
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <a
                className="mu-text-action"
                href={matchplayProfileUrl(user.matchplay_user_id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open on MatchPlay ↗
              </a>
              <button type="button" className="mu-text-action" onClick={handleSyncMatchplayProfile}>Re-sync</button>
            </span>
          </div>
          <div className="mu-stat-grid">
            <StatTile label="Rating" value={Math.round(rating.rating)} />
            <StatTile label="Class" value={rating.ratingClass} />
            <StatTile label="Win rate" value={`${(rating.efficiencyPercent * 100).toFixed(1)}%`} />
            <StatTile label="W-L" value={`${rating.winCount}-${rating.lossCount}`} />
            <StatTile label="Games" value={rating.gameCount} />
            <StatTile label="Deviation" value={`± ${rating.rd}`} />
          </div>
        </section>
      ) : (
        <section className="mu-card mu-card--empty">
          <p className="mu-empty-title">
            {user.matchplay_user_id ? 'MatchPlay profile not synced' : 'No MatchPlay ID on file'}
          </p>
          <p className="mu-empty-body">
            {user.matchplay_user_id
              ? 'Re-sync from Competition identity to pull rating, avatar and IFPA number.'
              : 'Add the ID in Competition identity to pull rating, avatar and IFPA number.'}
          </p>
        </section>
      )}

      <section className="mu-card mu-card--padded">
        <div className="mu-section-head">
          <p className="mu-section-label">PinGolf handicap</p>
          <button type="button" className="mu-text-action" onClick={handleOpenHandicapSeedModal}>Set seed</button>
        </div>
        <div className="mu-stat-grid">
          {golfStats.map((stat) => <StatTile key={stat.label} label={stat.label} value={stat.value} />)}
        </div>
      </section>

      <section className="mu-card mu-card--padded">
        <div className="mu-section-head">
          <p className="mu-section-label">Badges · {userBadges.length}</p>
          <button type="button" className="mu-text-action" onClick={handleOpenAwardBadgeModal}>Award</button>
        </div>
        {userBadges.length === 0 ? (
          <p className="mu-empty-body" style={{ margin: 0 }}>No badges earned yet.</p>
        ) : (
          <div className="mu-badge-list">
            {userBadges.map((badge) => (
              <span className="mu-badge" key={badge.id}>
                <span className="mu-badge-glyph"><BadgeIcon badge={badge} size={18} /></span>
                <span className="mu-badge-text">
                  <span className="mu-badge-name">{badge.name}</span>
                  <span className="mu-badge-date">{formatDate(badge.earned_at)}</span>
                </span>
                <button
                  type="button"
                  className="mu-badge-revoke"
                  aria-label={`Revoke ${badge.name}`}
                  onClick={() => handleRevokeBadge(badge)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </section>
    </>
  );

  const visibleTransactions = showAllTransactions ? transactions : transactions.slice(0, 3);

  const renderMoney = () => (
    <>
      {/* Membership, derived from payment evidence. The single place
          membership is decided, and the only one that may be trusted:
          `user.is_member` is a cache of what this computes. It renders its
          own empty state, so there is no second "not a member" card. */}
      <MembershipPanel
        userId={user.id}
        userName={`${user.preferred_name || user.first_name} ${user.last_name}`}
        memberSince={membershipSettings?.start_date}
      />

      <DSCard
        frame="bars"
        density="compact"
        headerTone="strong"
        flush
        title="Credits & tab"
        subtitle="Money the club holds for this player. Spent at the machine."
        footer={transactions.length > 3 ? (
          <DSCardFooterButton
            showArrow={false}
            onClick={() => setShowAllTransactions(!showAllTransactions)}
          >
            {showAllTransactions ? 'Show fewer' : `View all ${transactions.length} transactions`}
          </DSCardFooterButton>
        ) : null}
      >
        <div className="mu-card-body">
          <div className="mu-stat-grid">
            <div className="mu-stat">
              <span className="mu-stat-label">Balance</span>
              <span className={`mu-stat-value mu-stat-value--lg${negative ? ' is-negative' : ''}`}>
                {money(creditBalance)}
              </span>
            </div>
            <div className="mu-stat">
              <span className="mu-stat-label">Tab</span>
              <span className="mu-stat-value">{tabEnabled ? `Enabled · ${tabLimitLabel}` : 'Disabled'}</span>
            </div>
          </div>
          <div className="mu-button-row">
            <button type="button" className="mu-primary-button" onClick={() => setShowCreditsModal(true)}>
              Add credits
            </button>
            <button type="button" className="mu-outline-button" onClick={handleOpenTabSettingsModal}>
              Configure tab
            </button>
          </div>
        </div>
        <div>
          {transactions.length === 0 && <p className="mu-empty-line">No transactions yet.</p>}
          {visibleTransactions.map((txn) => {
            const amount = parseFloat(txn.amount);
            const isCredit = amount > 0;
            const isOpen = openTx === txn.id;
            const trail = txTrail(txn);
            return (
              <div className="mu-tx" key={txn.id}>
                <button
                  type="button"
                  className="mu-tx-toggle"
                  aria-expanded={isOpen}
                  onClick={() => setOpenTx(isOpen ? null : txn.id)}
                >
                  <span className={`mu-type-badge${isCredit ? ' is-credit' : ''}`}>
                    {txn.transaction_type || (isCredit ? 'credit' : 'debit')}
                  </span>
                  <span className="mu-row-main">
                    <span className="mu-row-title">{txn.description || (isCredit ? 'Credit added' : 'Debit')}</span>
                    <span className="mu-row-meta">{formatDateTime(txn.created_at)}</span>
                  </span>
                  <span className={`mu-amount${isCredit ? ' is-credit' : ''}`}>{signedMoney(amount)}</span>
                  <span className="mu-caret">{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && (
                  <div className="mu-tx-detail">
                    {trail.length > 0 && (
                      <div className="mu-trail">
                        {trail.map((chip) => <span className="mu-trail-chip" key={chip}>{chip}</span>)}
                      </div>
                    )}
                    <div className="mu-detail-actions">
                      <button
                        type="button"
                        className="mu-detail-button"
                        onClick={() => handleOpenEditTransaction(txn)}
                      >
                        Correct
                      </button>
                      <button
                        type="button"
                        className="mu-detail-button mu-detail-button--danger"
                        onClick={() => handleDeleteTransaction(txn.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DSCard>
    </>
  );

  const accessGroups = [
    {
      title: 'Account state',
      blurb: 'Whether this person can sign in, and whether dues apply.',
      rows: [
        {
          label: 'Account active',
          help: 'Off blocks sign-in and machine access immediately.',
          value: user.is_active ? 'Enabled' : 'Disabled',
          tone: user.is_active ? 'success' : 'warning',
          onAct: () => confirmThen({
            title: user.is_active ? 'Disable this account?' : 'Enable this account?',
            message: user.is_active
              ? 'They lose sign-in and machine access right away. Credits and history are kept.'
              : 'They regain sign-in and machine access right away.',
            confirmLabel: user.is_active ? 'Disable account' : 'Enable account',
            danger: user.is_active,
            run: handleToggleStatus
          })
        }
        // The Member and Complimentary toggles that used to sit here wrote
        // `is_member` straight onto the user, which made this screen a grant
        // path competing with the nightly sync and with onboarding. Membership
        // is decided in one place now, so those controls live in the Membership
        // section above: Grant comp, End membership, Grant membership anyway.
      ]
    },
    {
      title: 'Administrator',
      blurb: 'Full authority across the admin app. The only organisation-wide role.',
      rows: [{
        label: 'Administrator',
        help: 'Bypasses every other grant on this screen. Membership and tech tier are separate.',
        value: isAdmin ? 'On' : 'Off',
        tone: isAdmin ? 'brand' : 'muted',
        onAct: () => handleRoleChange(isAdmin ? NON_ADMIN_ROLE : ADMIN_ROLE)
      }]
    },
    {
      title: 'Machine access',
      blurb: 'Tech tier governs which maintenance tickets they can see and work.',
      rows: [{
        label: 'Tech tier',
        help: '0 none · 1 glass · 2 routine · 3 repair · 4 senior. Admins keep a real tier even though they bypass it.',
        value: `Tier ${user.tech_tier ?? 0}`,
        onAct: () => setPicker('tier')
      }]
    },
    {
      title: 'Feature grants',
      blurb: 'Scoped authority inside one area of the app. Always on for admins.',
      rows: [
        {
          label: 'Task delegate',
          help: 'Full admin authority inside Task Management only.',
          value: isAdmin || isTaskDelegate ? 'Enabled' : 'Disabled',
          tone: isAdmin ? 'muted' : 'default',
          locked: isAdmin,
          onAct: isAdmin
            ? () => showToast('Admins always hold this grant', 'info')
            : handleToggleTaskDelegate
        },
        {
          label: 'Maintenance organizer',
          help: 'Assign maintenance records, edit checklist templates, set intervals.',
          value: isAdmin || isMaintenanceOrganizer ? 'Enabled' : 'Disabled',
          tone: isAdmin ? 'muted' : 'default',
          locked: isAdmin,
          onAct: isAdmin
            ? () => showToast('Admins always hold this grant', 'info')
            : handleToggleMaintenanceOrganizer
        }
      ]
    },
    {
      title: 'Credit policy',
      blurb: 'How far the balance may go negative at the machine.',
      rows: [{
        label: 'Tab',
        help: tabEnabled
          ? `Enabled, ${tabLimitLabel}. Unlimited is for trusted members only.`
          : 'Disabled. Purchases are blocked when the balance is short.',
        value: tabEnabled ? 'Enabled' : 'Disabled',
        tone: tabEnabled ? 'warning' : 'default',
        onAct: handleOpenTabSettingsModal
      }]
    }
  ];

  const renderAccess = () => accessGroups.map((group) => (
    <DSCard
      key={group.title}
      frame="bars"
      density="compact"
      headerTone="strong"
      flush
      title={group.title}
      subtitle={group.blurb}
    >
      {group.rows.map((row) => <SettingRow key={row.label} {...row} />)}
    </DSCard>
  ));

  const renderOnboarding = () => {
    if (!application) return null;

    const steps = [
      {
        label: 'Orientation scheduled',
        done: Boolean(application.orientation_scheduled_date),
        at: application.orientation_scheduled_date,
        by: application.orientation_scheduled_by_first_name,
        mark: handleMarkOrientationScheduled,
        undo: handleUndoOrientationScheduled
      },
      {
        label: 'Orientation complete',
        done: Boolean(application.orientation_completed),
        at: application.orientation_completed_at,
        by: application.orientation_completed_by_first_name,
        mark: handleMarkOrientationComplete,
        undo: handleUndoOrientationComplete
      },
      {
        label: 'Payment setup',
        done: Boolean(application.payment_setup_complete),
        at: application.payment_setup_complete_at,
        by: application.payment_setup_complete_by_first_name,
        mark: handleMarkPaymentSetupComplete,
        undo: handleUndoPaymentSetup
      },
      {
        label: 'Master lock setup',
        done: Boolean(application.master_lock_setup),
        at: application.master_lock_setup_at,
        by: application.master_lock_setup_by_first_name,
        mark: handleMarkMasterLockComplete,
        undo: handleUndoMasterLockSetup
      }
    ];

    const areas = parseContributionAreas(application.contribution_areas);
    if (application.contribution_other) areas.push(`other: ${application.contribution_other}`);

    const availability = normalizeAvailability(application.orientation_availability);
    const hasAvailability = Object.values(availability).some((slots) => slots.length > 0);

    const appRows = [
      { label: 'Reason for joining', value: application.reason_for_joining || 'Not answered' },
      { label: 'Contribution areas', value: areas.length > 0 ? areas.join(', ') : 'Not answered' },
      {
        label: 'Been to the club before',
        value: application.visited_before === null || application.visited_before === undefined
          ? 'Not answered'
          : application.visited_before
            ? `Yes${application.visited_before_details ? `, ${application.visited_before_details}` : ''}`
            : 'No'
      },
      {
        label: 'Knows a current member',
        value: application.knows_member === null || application.knows_member === undefined
          ? 'Not answered'
          : application.knows_member
            ? `Yes${application.knows_member_name ? `, ${application.knows_member_name}` : ''}`
            : 'No'
      },
      {
        label: 'Application history',
        value: applicationHistory && applicationHistory.length > 1
          ? `${applicationHistory.length} applications on file`
          : 'This is their first application'
      }
    ];

    return (
      <>
        {applicationHistory && applicationHistory.length > 1 && (
          <section className="mu-card mu-card--padded">
            <DSSelect
              label="Application"
              value={selectedApplicationId || application.id}
              onChange={(e) => handleSwitchApplication(e.target.value)}
            >
              {applicationHistory.map((app) => (
                <option key={app.id} value={app.id}>
                  {formatDate(app.submitted_at)} - {app.status}{app.id === application.id ? ' (current)' : ''}
                </option>
              ))}
            </DSSelect>
          </section>
        )}

        <section className="mu-card mu-card--padded">
          <div className="mu-app-head">
            <span className={`mu-status-chip${application.status === 'closed' ? ' mu-status-chip--neutral' : ''}`}>
              {application.status === 'closed' ? 'Closed' : 'In progress'}
            </span>
            <span className="mu-app-submitted">Submitted {formatDate(application.submitted_at)}</span>
            <span className="mu-step-progress">{doneCount} of 4 steps</span>
          </div>
          <div className="mu-steps">
            {steps.map((step, index) => {
              const previousDone = index === 0 || steps[index - 1].done;
              return (
                <div className={`mu-step${step.done ? ' is-done' : ''}`} key={step.label}>
                  <span className="mu-step-dot">{step.done ? '✓' : index + 1}</span>
                  <span className="mu-row-main">
                    <span className="mu-step-label">{step.label}</span>
                    <span className="mu-step-meta">
                      {step.done
                        ? `${step.at ? formatDate(step.at) : 'Done'}${step.by ? ` · ${step.by}` : ''}`
                        : previousDone ? 'Ready to mark' : 'Waiting on previous step'}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={`mu-step-button${step.done ? ' mu-step-button--undo' : ''}`}
                    onClick={step.done ? step.undo : step.mark}
                  >
                    {step.done ? 'Undo' : 'Mark done'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mu-card mu-card--padded">
          <p className="mu-section-label" style={{ marginBottom: '10px' }}>Application</p>
          <div className="mu-app-rows">
            {appRows.map((row) => (
              <div key={row.label}>
                <span className="mu-app-row-label">{row.label}</span>
                <span className="mu-app-row-value">{row.value}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mu-card mu-card--padded">
          <p className="mu-section-label" style={{ marginBottom: '10px' }}>Orientation availability</p>
          {hasAvailability ? (
            <div className="mu-avail-grid">
              {SLOTS.map((slot) => (
                <React.Fragment key={slot}>
                  <span className="mu-avail-cell">{slot}</span>
                  {DAY_NAMES.map((day) => {
                    const on = (availability[day] || []).includes(slot);
                    return (
                      <span
                        className={`mu-avail-cell${on ? ' is-on' : ''}`}
                        key={`${slot}-${day}`}
                        title={`${day} ${slot}`}
                      >
                        {day.charAt(0)}
                      </span>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p className="mu-empty-body" style={{ margin: 0 }}>No availability information provided.</p>
          )}
          <p className="mu-preferred-time">
            Preferred time: {application.orientation_preferred_time || 'Not given'}
          </p>
        </section>

        <section className="mu-card mu-card--padded">
          <p className="mu-section-label" style={{ marginBottom: '10px' }}>Acknowledgements</p>
          <div className="mu-ack-list">
            <div className="mu-ack-row">
              <span className={`mu-ack-mark${application.rules_acknowledged_at ? '' : ' is-missing'}`}>
                {application.rules_acknowledged_at ? '✓' : '×'}
              </span>
              <span className="mu-ack-label">Club rules acknowledged</span>
              <span className="mu-ack-date">
                {application.rules_acknowledged_at ? formatDate(application.rules_acknowledged_at) : 'Not acknowledged'}
              </span>
            </div>
            <div className="mu-ack-row">
              <span className={`mu-ack-mark${application.agreement_accepted ? '' : ' is-missing'}`}>
                {application.agreement_accepted ? '✓' : '×'}
              </span>
              <span className="mu-ack-label">Membership agreement accepted</span>
              <span className="mu-ack-date">
                {application.agreement_accepted_at ? formatDate(application.agreement_accepted_at) : 'Not accepted'}
              </span>
            </div>
          </div>
        </section>

        {application.status !== 'closed' && (
          <button type="button" className="mu-destructive-button" onClick={handleCloseApplication}>
            Close application
          </button>
        )}
      </>
    );
  };

  const renderSupport = () => (
    <>
      <DSCard
        frame="bars"
        density="compact"
        headerTone="strong"
        flush
        title="Account repair"
        subtitle="Recoverable actions. Each one is confirmed first."
      >
        <SettingRow
          label="Regenerate QR code"
          help="Issues a new door and kiosk code. The old one stops working immediately."
          value="Regenerate"
          onAct={handleRegenerateQRCode}
        />
        <SettingRow
          label="Reset password"
          help="Emails a temporary password the user must change at next login."
          value="Reset"
          onAct={handleResetPassword}
        />
      </DSCard>

      <section className="mu-danger-zone">
        <p className="mu-danger-title">Danger zone</p>
        <p className="mu-danger-body">
          Deleting removes the account, credit history and payment record. This cannot be undone.
        </p>
        <button type="button" className="mu-danger-button" onClick={handleDeleteUser}>
          Delete this user
        </button>
      </section>
    </>
  );

  /* ---------- header ---------- */

  const accountTypeChip = user.is_member ? 'Full member' : application ? 'Applicant' : 'Guest';
  const subtitle = rawProfile?.user?.location || user.email;
  const displayName = `${user.preferred_name || user.first_name} ${user.last_name}`;
  const showLegalName = Boolean(user.preferred_name) && user.preferred_name !== user.first_name;

  /* ---------- profile sheet ---------- */

  const profileSheets = {
    identity: {
      title: 'Edit name and pronouns',
      body: 'The legal name stays on the membership record. The preferred name is what the club sees.',
      fields: [
        { key: 'firstName', label: 'First name', placeholder: 'Required' },
        { key: 'lastName', label: 'Last name', placeholder: 'Required' },
        { key: 'preferredName', label: 'Preferred name', placeholder: 'Optional' },
        { key: 'pronouns', label: 'Pronouns', placeholder: 'Optional, e.g. they/them' }
      ]
    },
    email: {
      title: 'Edit email',
      body: 'Saved to the member record immediately.',
      fields: [{ key: 'email', label: 'Email', type: 'email', placeholder: 'name@example.com' }]
    },
    phone: {
      title: 'Edit phone',
      body: 'Saved to the member record immediately.',
      fields: [{ key: 'phoneNumber', label: 'Phone', type: 'tel', placeholder: 'Optional', maxLength: 20 }]
    },
    bio: {
      title: 'Edit host bio',
      body: 'Shown on their event pages and on the hallway sign on nights they host.',
      fields: [{
        key: 'hostProfile',
        label: 'Host profile',
        multiline: true,
        placeholder: 'What should members know about hosting with them?'
      }]
    },
    stern: {
      title: 'Edit Stern username',
      body: 'Must match their Stern Insider account exactly.',
      fields: [{ key: 'sternUsername', label: 'Stern username', placeholder: 'Required for competitions' }]
    },
    matchplay: {
      title: 'Edit MatchPlay user ID',
      body: 'Saving re-syncs the profile and auto-fills the IFPA ID.',
      fields: [{ key: 'matchplayUserId', label: 'MatchPlay user ID', placeholder: 'Optional' }]
    },
    ifpa: {
      title: 'Edit IFPA player ID',
      body: 'Saved to the member record immediately.',
      fields: [{ key: 'ifpaPlayerId', label: 'IFPA player ID', placeholder: 'Optional' }]
    }
  };

  const activeProfileSheet = isEditingProfile && profileField ? profileSheets[profileField] : null;

  /* ---------- charge sheet state ---------- */

  // An admin charging from here is never blocked by the balance or the tab: it
  // is recording a deal already made with the member, often one that takes
  // them negative on purpose. The sheet just says where the balance will land.
  const chargeBody = (amount) => {
    const parts = [`Balance ${money(creditBalance)}`];
    if (amount > 0) {
      parts.push(`charge ${money(amount)}`);
      if (creditBalance - amount < 0) parts.push(`leaves ${money(creditBalance - amount)}`);
    }
    return parts.join(' · ');
  };
  const closeChargeSheet = () => setChargeForm({ kind: null, amount: '', description: '' });

  const filteredAwardable = awardableBadges.filter((badge) =>
    badge.name.toLowerCase().includes(badgeSearchTerm.trim().toLowerCase()));

  return (
    <div className="mu-page">
      <div className="mu-shell">
        <div className="mu-header">
          <div className="mu-header-top">
            <Link to="/users" className="mu-back">‹ Users</Link>
            <span className="mu-user-id">#{userId}</span>
          </div>

          <div className="mu-identity">
            {/* Their own photo first, then their MatchPlay avatar, then initials. */}
            <UserAvatar person={user} className="mu-avatar" fallbackSrc={rawProfile?.user?.avatar} />
            <div className="mu-identity-body">
              <div className="mu-name-row">
                <h1 className="mu-name">{displayName}</h1>
                {user.pronouns && <span className="mu-pronouns">{user.pronouns}</span>}
              </div>
              {showLegalName && (
                <p className="mu-legal-name">Legal name: {user.first_name} {user.last_name}</p>
              )}
              <p className="mu-subtitle">{subtitle}</p>
              <div className="mu-chips">
                <span className="mu-chip mu-chip--brand">{accountTypeChip}</span>
                <span className="mu-chip mu-chip--neutral">
                  {titleCase(user.role)} · Tier {user.tech_tier ?? 0}
                </span>
                <span className={`mu-chip mu-chip--${user.is_active ? 'success' : 'warning'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                {negative && tabEnabled && <span className="mu-chip mu-chip--warning">Tab active</span>}
              </div>
            </div>
            <button
              type="button"
              className="mu-edit-identity"
              aria-label="Edit name and pronouns"
              onClick={openProfileSheet('identity')}
            >
              <DSIcon name="edit" />
            </button>
          </div>

          <div className="mu-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                type="button"
                role="tab"
                key={t.id}
                aria-selected={tab === t.id}
                className={`mu-tab${tab === t.id ? ' is-active' : ''}`}
                onClick={() => goTab(t.id)}
              >
                {t.label}
                {t.count && <span className="mu-tab-count">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>

        {!hasStern && (
          <div className="mu-banner">
            <span className="mu-banner-mark"><DSIcon name="warning" size="sm" /></span>
            <div>
              <p className="mu-banner-title">No Stern username</p>
              <p className="mu-banner-body">
                Blocks competition entry. Add it in Overview → Competition identity.
              </p>
            </div>
          </div>
        )}

        <div className="mu-panel">
          {tab === 'desk' && renderDesk()}
          {tab === 'overview' && renderOverview()}
          {tab === 'money' && renderMoney()}
          {tab === 'access' && renderAccess()}
          {tab === 'onboarding' && renderOnboarding()}
          {tab === 'support' && renderSupport()}
        </div>
      </div>

      {/* ---------------- sheets ---------------- */}

      {activeProfileSheet && (
        <Sheet
          title={activeProfileSheet.title}
          body={activeProfileSheet.body}
          confirmLabel="Save"
          onConfirm={handleSaveProfile}
          onClose={closeProfileSheet}
        >
          {activeProfileSheet.fields.map((field) => (
            field.multiline ? (
              <DSTextarea
                key={field.key}
                label={field.label}
                rows="8"
                placeholder={field.placeholder}
                value={profileForm[field.key]}
                onChange={(e) => setProfileForm({ ...profileForm, [field.key]: e.target.value })}
              />
            ) : (
              <DSInput
                key={field.key}
                label={field.label}
                type={field.type || 'text'}
                maxLength={field.maxLength}
                placeholder={field.placeholder}
                value={profileForm[field.key]}
                onChange={(e) => setProfileForm({ ...profileForm, [field.key]: e.target.value })}
              />
            )
          ))}
        </Sheet>
      )}

      {picker === 'tier' && (
        <Sheet
          title="Set tech tier"
          body="Changes which machine tickets are visible to them."
          onClose={() => setPicker(null)}
        >
          <div className="mu-picker">
            {TECH_TIERS.map((tier) => (
              <button
                type="button"
                key={tier.value}
                className={`mu-picker-option${(user.tech_tier ?? 0) === tier.value ? ' is-current' : ''}`}
                onClick={() => {
                  setPicker(null);
                  if (tier.value !== (user.tech_tier ?? 0)) handleTechTierChange(tier.value);
                }}
              >
                {tier.label}
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {showCreditsModal && (
        <Sheet
          title="Add credits"
          body={`Balance ${money(creditBalance)}`}
          confirmLabel="Add credits"
          onConfirm={handleAddCredits}
          onClose={() => {
            setShowCreditsModal(false);
            setCreditsForm({ amount: '', description: '', sendNotification: true });
          }}
        >
          <DSInput
            label="Amount"
            type="number"
            step="0.01"
            placeholder="20.00"
            value={creditsForm.amount}
            onChange={(e) => setCreditsForm({ ...creditsForm, amount: e.target.value })}
          />
          <DSInput
            label="Description"
            type="text"
            placeholder="Credit top-up"
            value={creditsForm.description}
            onChange={(e) => setCreditsForm({ ...creditsForm, description: e.target.value })}
          />
          <SheetCheck
            label="Send notification to the member"
            checked={creditsForm.sendNotification}
            onChange={(next) => setCreditsForm({ ...creditsForm, sendNotification: next })}
          />
        </Sheet>
      )}

      {showGuestPassModal && (
        <Sheet
          title="Guest day pass"
          body={chargeBody(GUEST_PASS_PRICE)}
          confirmLabel="Charge"
          onConfirm={handleGuestPassPurchase}
          onClose={() => {
            setShowGuestPassModal(false);
            setGuestPassForm({ guestName: '' });
          }}
        >
          <DSInput
            label="Guest's name"
            type="text"
            placeholder="Required"
            value={guestPassForm.guestName}
            onChange={(e) => setGuestPassForm({ ...guestPassForm, guestName: e.target.value })}
          />
        </Sheet>
      )}

      {showPartOrderModal && (
        <Sheet
          title="Part order"
          body={chargeBody(parseFloat(partOrderForm.amount))}
          confirmLabel="Charge"
          onConfirm={handlePartOrderCharge}
          onClose={() => {
            setShowPartOrderModal(false);
            setPartOrderForm({ amount: '', description: '' });
          }}
        >
          <DSInput
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            placeholder="22.50"
            value={partOrderForm.amount}
            onChange={(e) => setPartOrderForm({ ...partOrderForm, amount: e.target.value })}
          />
          <DSInput
            label="Description"
            type="text"
            helpText='Defaults to "Part order" if left blank.'
            value={partOrderForm.description}
            onChange={(e) => setPartOrderForm({ ...partOrderForm, description: e.target.value })}
          />
        </Sheet>
      )}

      {chargeForm.kind === 'tournament' && (
        <Sheet
          title="Tournament fee"
          body={chargeBody(TOURNAMENT_FEE)}
          confirmLabel="Charge"
          onConfirm={handleDeskCharge}
          onClose={closeChargeSheet}
        >
          <DSInput
            label="Tournament"
            type="text"
            placeholder="Optional"
            value={chargeForm.description}
            onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
          />
        </Sheet>
      )}

      {chargeForm.kind === 'service' && (
        <Sheet
          title="Service"
          body={chargeBody(parseFloat(chargeForm.amount))}
          confirmLabel="Charge"
          confirmDisabled={!(parseFloat(chargeForm.amount) > 0)}
          onConfirm={handleDeskCharge}
          onClose={closeChargeSheet}
        >
          <DSInput
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            placeholder="75.00"
            value={chargeForm.amount}
            onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
          />
          <DSInput
            label="Work done"
            type="text"
            placeholder="Optional, e.g. flipper rebuild on their Medieval Madness"
            value={chargeForm.description}
            onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
          />
        </Sheet>
      )}

      {chargeForm.kind === 'other' && (
        <Sheet
          title="Other charge"
          body={chargeBody(parseFloat(chargeForm.amount))}
          confirmLabel="Charge"
          confirmDisabled={!chargeForm.description.trim() || !(parseFloat(chargeForm.amount) > 0)}
          onConfirm={handleDeskCharge}
          onClose={closeChargeSheet}
        >
          <DSInput
            label="What it's for"
            type="text"
            placeholder="Required"
            value={chargeForm.description}
            onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
          />
          <DSInput
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            placeholder="10.00"
            value={chargeForm.amount}
            onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
          />
        </Sheet>
      )}

      {duesFor && (
        <RecordCashSheet
          member={duesFor}
          defaultPaidWith="credits"
          onClose={() => setDuesFor(null)}
          onDone={() => { setDuesFor(null); loadUserData(); }}
        />
      )}

      {showEditTransactionModal && (
        <Sheet
          title="Correct transaction"
          body="A negative amount records a debit. The original entry stays in the audit trail."
          confirmLabel="Save correction"
          onConfirm={handleEditTransaction}
          onClose={() => {
            setShowEditTransactionModal(false);
            setEditTransactionForm({ id: null, amount: '', description: '' });
          }}
        >
          <DSInput
            label="Amount"
            type="number"
            step="0.01"
            helpText="Use negative values for debits, positive for credits."
            value={editTransactionForm.amount}
            onChange={(e) => setEditTransactionForm({ ...editTransactionForm, amount: e.target.value })}
          />
          <DSInput
            label="Description"
            type="text"
            value={editTransactionForm.description}
            onChange={(e) => setEditTransactionForm({ ...editTransactionForm, description: e.target.value })}
          />
        </Sheet>
      )}

      {showTabSettingsModal && (
        <Sheet
          title="Configure tab"
          body="Set how far the balance may go negative at the machine."
          confirmLabel="Save tab settings"
          onConfirm={handleUpdateTabSettings}
          onClose={() => setShowTabSettingsModal(false)}
        >
          <SheetCheck
            label="Enable tab (allow a negative balance)"
            checked={tabSettingsForm.tabEnabled}
            onChange={(next) => setTabSettingsForm({
              ...tabSettingsForm,
              tabEnabled: next,
              ...(next ? {} : { unlimited: false, tabLimit: '' })
            })}
          />
          {tabSettingsForm.tabEnabled && (
            <>
              <SheetCheck
                label="Unlimited, trusted members only"
                checked={tabSettingsForm.unlimited}
                onChange={(next) => setTabSettingsForm({
                  ...tabSettingsForm,
                  unlimited: next,
                  tabLimit: next ? '' : tabSettingsForm.tabLimit
                })}
              />
              {!tabSettingsForm.unlimited && (
                <DSInput
                  label="Maximum negative balance"
                  helpText="Enter 20 to allow the balance down to -$20.00."
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="20.00"
                  value={tabSettingsForm.tabLimit}
                  onChange={(e) => setTabSettingsForm({ ...tabSettingsForm, tabLimit: e.target.value })}
                />
              )}
            </>
          )}
        </Sheet>
      )}

      {showMemberSinceSheet && (
        <Sheet
          title="Edit member since"
          body="The date they became a member. Used for tenure and recognition, not for payment status."
          confirmLabel="Save date"
          onConfirm={handleUpdateMemberSince}
          onClose={() => setShowMemberSinceSheet(false)}
        >
          <DSInput
            label="Member since"
            type="date"
            value={memberSinceDraft}
            onChange={(e) => setMemberSinceDraft(e.target.value)}
            helpText="Payment evidence still decides whether they are a member right now."
          />
        </Sheet>
      )}

      {/* The "Membership settings" sheet used to live here, holding the only
          renewal-day field in the app - and nothing ever opened it, so no
          member could be given a renewal date. It now sits on the membership
          panel beside "Pays by", which is where an admin looks for how and when
          somebody pays. */}

      {showPaymentModal && (
        <Sheet
          title="Record monthly payment"
          body="Logged against the month, not the date received."
          confirmLabel="Record payment"
          onConfirm={handleRecordPayment}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentForm({
              paymentDate: new Date().toISOString().split('T')[0],
              paymentMonth: new Date().toISOString().slice(0, 7) + '-01',
              amount: '50.00',
              notes: ''
            });
          }}
        >
          <DSInput
            label="Payment month"
            type="month"
            value={paymentForm.paymentMonth.slice(0, 7)}
            onChange={(e) => setPaymentForm({ ...paymentForm, paymentMonth: `${e.target.value}-01` })}
          />
          <DSInput
            label="Payment date"
            type="date"
            value={paymentForm.paymentDate}
            onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
          />
          <DSInput
            label="Amount"
            type="number"
            step="0.01"
            placeholder="50.00"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
          />
          <DSTextarea
            label="Notes"
            rows="3"
            placeholder="Optional"
            value={paymentForm.notes}
            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
          />
        </Sheet>
      )}

      {showEditPaymentModal && selectedPayment && (
        <Sheet
          title="Re-file payment"
          body="Moves this payment to a different month. Amount and date are unchanged."
          record={[
            { label: 'Current month', value: formatMonth(selectedPayment.payment_month) },
            { label: 'Paid', value: formatDate(selectedPayment.payment_date) },
            { label: 'Amount', value: `$${parseFloat(selectedPayment.amount).toFixed(2)}` }
          ]}
          confirmLabel="Re-file payment"
          onConfirm={handleEditPayment}
          onClose={() => {
            setShowEditPaymentModal(false);
            setSelectedPayment(null);
            setEditPaymentForm({ id: null, paymentMonth: '' });
          }}
        >
          <DSInput
            label="New payment month"
            type="month"
            value={editPaymentForm.paymentMonth.slice(0, 7)}
            onChange={(e) => setEditPaymentForm({ ...editPaymentForm, paymentMonth: `${e.target.value}-01` })}
          />
        </Sheet>
      )}

      {showDeletePaymentModal && selectedPayment && (
        <Sheet
          title="Delete payment?"
          body="This cannot be undone."
          record={[
            { label: 'Month', value: formatMonth(selectedPayment.payment_month) },
            { label: 'Paid', value: formatDate(selectedPayment.payment_date) },
            { label: 'Amount', value: `$${parseFloat(selectedPayment.amount).toFixed(2)}` },
            { label: 'Collected by', value: selectedPayment.collected_by_username || 'Unknown' },
            ...(selectedPayment.notes ? [{ label: 'Notes', value: selectedPayment.notes }] : [])
          ]}
          confirmLabel="Delete payment"
          danger
          onConfirm={handleDeletePayment}
          onClose={() => {
            setShowDeletePaymentModal(false);
            setSelectedPayment(null);
          }}
        />
      )}

      {showHandicapSeedModal && (
        <Sheet
          title="Set handicap seed"
          body="Stops new players starting at 0 before they have real cards. Leave the seed blank to clear it."
          confirmLabel="Save seed"
          onConfirm={handleUpdateHandicapSeed}
          onClose={() => setShowHandicapSeedModal(false)}
        >
          {/* Direction and magnitude are separate controls so the field reads the same way
              the scorecard does. The old single input took the raw vs-par average, so an
              admin typed -2 and the app then showed the player +2. */}
          <DSSelect
            label="Seed direction"
            helpText="Which way the seed moves their card, exactly as it will read on the scorecard."
            value={handicapSeedForm.seedSign}
            onChange={(e) => setHandicapSeedForm({ ...handicapSeedForm, seedSign: e.target.value })}
          >
            <option value="+">+ Strokes added (spots the field)</option>
            <option value="-">- Strokes taken (catch-up allowance)</option>
          </DSSelect>
          <DSInput
            label="Seed handicap (strokes applied)"
            helpText="How many strokes, as a plain number. Leave blank to clear the seed."
            type="number"
            min="0"
            step="0.1"
            placeholder="e.g. 2"
            value={handicapSeedForm.seed}
            onChange={(e) => setHandicapSeedForm({ ...handicapSeedForm, seed: e.target.value })}
          />
          <DSInput
            label="Seed weight (phantom rounds)"
            helpText="How many phantom rounds the seed counts as before real cards dilute it. Defaults to 3."
            type="number"
            min="1"
            step="1"
            placeholder="3"
            value={handicapSeedForm.seedWeight}
            onChange={(e) => setHandicapSeedForm({ ...handicapSeedForm, seedWeight: e.target.value })}
          />
          <DSTextarea
            label="Reason"
            rows="2"
            placeholder="Optional"
            value={handicapSeedForm.reason}
            onChange={(e) => setHandicapSeedForm({ ...handicapSeedForm, reason: e.target.value })}
          />
        </Sheet>
      )}

      {showAwardBadgeModal && (
        <Sheet
          title="Award a badge"
          body="Only badges this user does not already hold are listed."
          confirmLabel="Award badge"
          confirmDisabled={awardingBadge || !badgeToAwardId}
          onConfirm={handleAwardBadge}
          onClose={() => {
            setShowAwardBadgeModal(false);
            setBadgeToAwardId('');
            setBadgeSearchTerm('');
          }}
        >
          {awardableBadges.length === 0 ? (
            <p className="mu-empty-body" style={{ margin: 0 }}>
              No badges available to award. This user already has every active badge, or none have been
              created yet (manage the catalog under Badges in the admin nav).
            </p>
          ) : (
            <>
              <DSInput
                label="Search badges"
                type="text"
                placeholder="Type to filter"
                value={badgeSearchTerm}
                onChange={(e) => setBadgeSearchTerm(e.target.value)}
              />
              <div className="mu-badge-picker">
                {filteredAwardable.map((badge) => (
                  <button
                    type="button"
                    key={badge.id}
                    className={`mu-picker-option${String(badge.id) === badgeToAwardId ? ' is-current' : ''}`}
                    onClick={() => setBadgeToAwardId(String(badge.id))}
                  >
                    <span className="mu-badge-glyph"><BadgeIcon badge={badge} size={18} /></span>
                    {badge.name}
                  </button>
                ))}
                {filteredAwardable.length === 0 && (
                  <p className="mu-empty-body" style={{ margin: 0 }}>
                    No badges match "{badgeSearchTerm}".
                  </p>
                )}
              </div>
            </>
          )}
        </Sheet>
      )}

      {showPasswordResetModal && passwordResetResult && (
        <Sheet
          title="Password reset"
          body="Share this only with the account holder."
          secret={passwordResetResult.tempPassword}
          secretNote={passwordResetResult.emailSent
            ? 'Email sent successfully. The user must change this at next login.'
            : 'Email could not be sent. Share the password with the user directly. They must change it at next login.'}
          onCopySecret={() => {
            navigator.clipboard.writeText(passwordResetResult.tempPassword);
            showToast('Password copied to clipboard', 'success');
          }}
          cancelLabel="Done"
          onClose={() => {
            setShowPasswordResetModal(false);
            setPasswordResetResult(null);
          }}
        />
      )}

      {confirmState && (
        <Sheet
          title={confirmState.title}
          body={confirmState.message}
          confirmLabel={confirmState.confirmLabel || 'Confirm'}
          confirmDisabled={confirmSubmitting}
          danger={confirmState.danger}
          onConfirm={confirmState.onConfirm}
          onClose={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default ManageUser;
